import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { getChangeRequestBranchId } from '@gitbook/app-hive';
import {
    CONTENT_DEFAULT_BRANCH,
    type ContentBranchHistoryItem,
    getRevisionCreationTime,
} from '@gitbook/hive-core';
import { useHiveBranchHistory } from '@gitbook/hive-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateIcon,
    type SectionRenderItemInfo,
    TimelineList,
} from '@gitbook/spine-react';

import { HiveBranchHistoryItem } from './HiveBranchHistoryItem';

/**
 * Timeline representing the history of a branch.
 */
export function HiveBranchHistory(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;

    /** The initial revision id to load. */
    initialRevisionId: string;

    /** The ID of the revision that is currently active. */
    activeRevisionId?: string;

    /** The filter to apply to the history. */
    filter?: {
        git?: boolean;
    };

    /** The blank slate to show when there is no history. */
    emptyState?: React.ReactNode;

    /** Whether the branch is read only. */
    readOnly?: boolean;
}) {
    const {
        space,
        changeRequest,
        initialRevisionId,
        activeRevisionId,
        filter = { git: false },
        readOnly = false,
        emptyState = <HiveBranchHistoryEmptyState />,
    } = props;

    const { history, loadMore } = useHiveBranchHistory({
        contentId: space.id,
        branchId: changeRequest
            ? getChangeRequestBranchId(changeRequest.id)
            : CONTENT_DEFAULT_BRANCH,
        initialPage: initialRevisionId,
        limit: 10,
        git: filter.git,
        stopAt: changeRequest?.revisionInitial,
    });

    const renderItem = React.useCallback(
        ({ item }: SectionRenderItemInfo<ContentBranchHistoryItem>) => {
            return (
                <HiveBranchHistoryItem
                    space={space}
                    changeRequest={changeRequest}
                    revisionChanges={item}
                    active={activeRevisionId === item.revision.id}
                    readOnly={readOnly}
                />
            );
        },
        [space, changeRequest, activeRevisionId, readOnly]
    );

    if (history.length === 0) {
        return <>{emptyState}</>;
    }

    return (
        <div className="flex flex-col p-2">
            <TimelineList
                data={history}
                getItemTime={getRevisionTime}
                getItemId={(item) => item.revision.id}
                renderItem={renderItem}
                dependencies={[activeRevisionId]}
            />
            {loadMore ? (
                <div className="flex flex-col p-3">
                    <Button
                        kind="secondary"
                        label="Load more"
                        trailingIcon={Icon.ArrowDown}
                        onPress={loadMore}
                    >
                        Load more
                    </Button>
                </div>
            ) : null}
        </div>
    );
}

function getRevisionTime(revisionChanges: ContentBranchHistoryItem) {
    const { revision } = revisionChanges;
    return getRevisionCreationTime(revision);
}

function HiveBranchHistoryEmptyState() {
    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.History />
            </EmptyStateIcon>
            <Description>No changes have been made yet.</Description>
        </EmptyState>
    );
}
